import { DeleteOutline } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import { useSelector } from 'react-redux';
import useUnitProvider from '/@/core/context/unit';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { plantedAreaForForm } from '/@/hooks/geometry/area';
import { useProducts } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { getSessionBrands } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import { type CropMonitoringFormType, MAX_ENVIRONMENT, MIN_ENVIRONMENT } from '../types';
import { getEnvironmentFromSameCropMonitoring } from '../utils';
import DensityInput from './DensityInput';
import EnvironmentInput from './EnvironmentInput';
import PlantedAreaInput from './PlantedAreaInput';
import PlantingDateInput from './PlantingDateInput';
import ProductSelector from './ProductSelector';
import TechnologySelector from './TechnologySelector';

type HybridBlockProps = {
    form: UseFormReturn<CropMonitoringFormType>;
    index: number;
    activeField: Field;
    crop: CropMonitoringFormType['crop'];
    canRemove: boolean;
    onRemove: () => void;
    onlyEdit: boolean;
    /** Hidden for non-corn crops, which show the plain fields without the hybrid framing. */
    showHeader?: boolean;
    /** Locks report-owned fields (date/area/density) read-only for FieldView plantings. */
    lockReportFields?: boolean;
};

const HybridBlock = ({
    form,
    index,
    activeField,
    crop,
    canRemove,
    onRemove,
    onlyEdit,
    showHeader = true,
    lockReportFields = false,
}: HybridBlockProps) => {
    const units = useUnitProvider();
    const sessionBrands = useSelector(getSessionBrands);
    const { dirtyFields } = useFormState({ control: form.control });

    const base = `hybrids.${index}` as const;
    const plantingDate = form.watch(`${base}.plantingDate`);
    const environment = form.watch(`${base}.environment`);
    const product = form.watch(`${base}.product`);
    const technology = form.watch(`${base}.technology`);
    const plantedArea = form.watch(`${base}.plantedArea`);
    const density = form.watch(`${base}.density`);

    const errors = form.formState.errors.hybrids?.[index];
    const isFirst = index === 0;
    // Non-first hybrids must be planted within a window of the first hybrid's date (CUL-1505).
    const firstPlantingDate = form.watch('hybrids.0.plantingDate');

    // Products for this hybrid (driven by its own planting date + environment).
    const debouncedEnvironment = useDebounce(environment, 1000);
    const productsQuery = useProducts({
        lat: activeField?.lat?.toString() || '',
        lng: activeField?.lng?.toString() || '',
        crop: crop || 'corn',
        plantingDate: plantingDate ? dayjs(plantingDate).format('YYYY-MM-DD') : '',
        brandCodename: (sessionBrands[crop || 'corn'] || [])
            .map((brand: { codename?: string }) => brand.codename)
            .filter(Boolean)
            .join(','),
        environment: debouncedEnvironment?.toString() || '',
    });

    // Technologies associated with the selected product (CornProductProfile.technologies,
    // delivered by /core/v1/products/, each with id + name). Used directly as the
    // Technology options -- NOT intersected with the generic catalog, which is brand/
    // displayInCatalog-filtered and would wrongly drop a product's own technologies.
    const productTechnologies = useMemo<{ id: number; name?: string; displayInCatalog?: boolean }[] | undefined>(() => {
        if (typeof product !== 'number' || product === -1 || !productsQuery.data) return undefined;
        const all = [...(productsQuery.data.featuredProducts || []), ...(productsQuery.data.otherProducts || [])];
        const selected = all.find((p: { id: number }) => p.id === product);
        const techs =
            (selected?.technologies as { id: number; name?: string; displayInCatalog?: boolean }[] | undefined) || [];
        return techs.length ? techs : undefined;
    }, [product, productsQuery.data]);

    // If the picked technology isn't among the product's technologies (e.g. after
    // switching product), clear it so an invalid value isn't submitted. When the
    // product has none, every option is valid, so nothing is cleared.
    useEffect(() => {
        if (!productTechnologies || technology == null) return;
        // Match the options the selector shows: the product's technologies that are in
        // the catalog. If none are, the selector falls back to the full catalog, so any
        // value is valid and nothing is cleared.
        const available = productTechnologies.filter((t) => t.displayInCatalog !== false);
        if (!available.length) return;
        if (!available.some((t) => t.id === technology)) {
            form.setValue(`${base}.technology`, undefined, { shouldValidate: true });
        }
    }, [productTechnologies, technology, form, base]);

    // Auto-set environment from the latest past campaign (first hybrid only).
    const envDirty = dirtyFields.hybrids?.[index]?.environment;
    useEffect(() => {
        if (!isFirst || onlyEdit || !activeField || !crop || envDirty) return;
        const inferred = getEnvironmentFromSameCropMonitoring(activeField, crop);
        if (inferred !== undefined) {
            form.setValue(`${base}.environment`, inferred, { shouldDirty: false, shouldValidate: true });
            form.clearErrors(`${base}.environment`);
        }
    }, [isFirst, onlyEdit, activeField, crop, envDirty, form, base]);

    // Auto-set planted area from the field geometry (first hybrid only).
    const areaDirty = dirtyFields.hybrids?.[index]?.plantedArea;
    useEffect(() => {
        if (!isFirst || onlyEdit || areaDirty) return;
        const value = plantedAreaForForm(units, { fieldM2: activeField?.area });
        if (value == null) return;
        form.setValue(`${base}.plantedArea`, value, { shouldDirty: false });
    }, [isFirst, onlyEdit, activeField?.area, areaDirty, form, units, base]);

    // Reset product when environment goes out of range (corn only).
    useEffect(() => {
        if (crop === 'corn' && environment && (environment < MIN_ENVIRONMENT || environment > MAX_ENVIRONMENT)) {
            form.setValue(`${base}.product`, undefined);
        }
    }, [environment, form, crop, base]);

    const [convertedMinEnv, convertedMaxEnv] = useMemo(
        () => [units.toYield(MIN_ENVIRONMENT, 'corn', 2).value, units.toYield(MAX_ENVIRONMENT, 'corn', 2).value],
        [units]
    );

    const isProductDisabled = useMemo(() => {
        if (!environment || !plantingDate) return true;
        const env = units.toYield(environment, 'corn', 2).value;
        return env < convertedMinEnv || env > convertedMaxEnv;
    }, [environment, plantingDate, units, convertedMinEnv, convertedMaxEnv]);

    const isTechnologyDisabled = isProductDisabled || !product;

    return (
        <Stack spacing={2} sx={{ width: '100%' }} data-testid={`hybrid-block-${index}`}>
            {showHeader && (
                <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{ backgroundColor: 'primary.infoBackground', p: 1 }}
                >
                    <Typography variant='subtitle2' color='primary' fontWeight='bold' letterSpacing={1}>
                        {_('HÍBRIDO {{n}}', { n: index + 1 })}
                    </Typography>
                    {canRemove && (
                        <IconButton
                            size='small'
                            onClick={onRemove}
                            aria-label={_('Eliminar híbrido')}
                            data-testid={`hybrid-block-${index}-remove`}
                        >
                            <DeleteOutline fontSize='small' />
                        </IconButton>
                    )}
                </Stack>
            )}

            <Grid2 container spacing={2} sx={{ width: '100%' }}>
                <Grid2 xs={6}>
                    <PlantingDateInput
                        showValidDateHelperText={false}
                        value={plantingDate}
                        anchorDate={isFirst ? undefined : firstPlantingDate}
                        onChange={(date: string) => {
                            form.setValue(`${base}.plantingDate`, date);
                            form.clearErrors(`${base}.plantingDate`);
                        }}
                        field={activeField}
                        crop={crop}
                        error={errors?.plantingDate?.message as string}
                        disabled={lockReportFields}
                    />
                </Grid2>
                <Grid2 xs={6}>
                    <EnvironmentInput
                        crop={crop || 'corn'}
                        value={environment}
                        onChange={(value: number | undefined) => {
                            form.setValue(`${base}.environment`, value, { shouldValidate: true, shouldDirty: true });
                            if (value) form.clearErrors(`${base}.environment`);
                        }}
                        units={units}
                        error={errors?.environment?.message as string}
                        minEnvironment={MIN_ENVIRONMENT}
                        maxEnvironment={MAX_ENVIRONMENT}
                    />
                </Grid2>
            </Grid2>

            <Grid2 container spacing={2} sx={{ width: '100%' }}>
                <Grid2 xs={crop === 'corn' ? 6 : 12}>
                    <ProductSelector
                        crop={crop}
                        value={product}
                        onChange={(value: number | undefined | string) => {
                            form.setValue(`${base}.product`, value, { shouldValidate: true });
                            form.clearErrors(`${base}.product`);
                        }}
                        productsQuery={productsQuery}
                        disabled={isProductDisabled}
                        units={units}
                        minEnvironment={MIN_ENVIRONMENT}
                        maxEnvironment={MAX_ENVIRONMENT}
                        environment={environment}
                        error={errors?.product?.message as string}
                    />
                </Grid2>
                {crop === 'corn' && (
                    <Grid2 xs={6}>
                        <TechnologySelector
                            value={technology}
                            onChange={(value: number | undefined) => {
                                form.setValue(`${base}.technology`, value, { shouldValidate: true });
                                form.clearErrors(`${base}.technology`);
                            }}
                            disabled={isTechnologyDisabled}
                            productTechnologies={productTechnologies}
                            error={errors?.technology?.message as string}
                        />
                    </Grid2>
                )}
            </Grid2>

            <Grid2 container spacing={2} sx={{ width: '100%' }}>
                <Grid2 xs={6}>
                    <PlantedAreaInput
                        value={plantedArea}
                        onChange={(value: number | undefined) => {
                            form.setValue(`${base}.plantedArea`, value, { shouldValidate: true, shouldDirty: true });
                            form.clearErrors(`${base}.plantedArea`);
                        }}
                        units={units}
                        error={errors?.plantedArea?.message as string}
                        disabled={lockReportFields}
                    />
                </Grid2>
                <Grid2 xs={6}>
                    <DensityInput
                        crop={crop || 'corn'}
                        value={density}
                        onChange={(value: number | undefined) => {
                            form.setValue(`${base}.density`, value, { shouldValidate: true });
                            form.clearErrors(`${base}.density`);
                        }}
                        units={units}
                        error={errors?.density?.message as string}
                        disabled={lockReportFields}
                    />
                </Grid2>
            </Grid2>
        </Stack>
    );
};

export default HybridBlock;
